/**
 * Currencies a tenant may keep its books in.
 *
 * The list is deliberately explicit rather than "any ISO 4217 code": every entry here has been
 * checked for its minor-unit exponent, and a currency nobody has verified is worse than one that
 * is simply absent.
 */

/**
 * How many decimal places the currency's minor unit has.
 *
 * Not always 2. The Gulf dinars are thousandths — a Kuwaiti fils is 1/1000 of a dinar, not 1/100 —
 * so anything that hardcodes `× 100` silently misprices by a factor of ten in three of the
 * currencies below. Everything that converts between major and minor units must read this.
 */
export interface CurrencyDefinition {
  code: string;
  name: string;
  nameAr: string;
  symbol: string;
  minorUnitExponent: number;
}

export const CURRENCIES: readonly CurrencyDefinition[] = [
  { code: 'EGP', name: 'Egyptian Pound', nameAr: 'جنيه مصري', symbol: 'E£', minorUnitExponent: 2 },
  { code: 'AED', name: 'UAE Dirham', nameAr: 'درهم إماراتي', symbol: 'د.إ', minorUnitExponent: 2 },
  { code: 'SAR', name: 'Saudi Riyal', nameAr: 'ريال سعودي', symbol: 'ر.س', minorUnitExponent: 2 },
  { code: 'USD', name: 'US Dollar', nameAr: 'دولار أمريكي', symbol: '$', minorUnitExponent: 2 },
  { code: 'EUR', name: 'Euro', nameAr: 'يورو', symbol: '€', minorUnitExponent: 2 },
  {
    code: 'GBP',
    name: 'Pound Sterling',
    nameAr: 'جنيه إسترليني',
    symbol: '£',
    minorUnitExponent: 2,
  },
  { code: 'QAR', name: 'Qatari Riyal', nameAr: 'ريال قطري', symbol: 'ر.ق', minorUnitExponent: 2 },
  // The Gulf dinars and the Jordanian dinar divide into thousandths, not hundredths.
  {
    code: 'KWD',
    name: 'Kuwaiti Dinar',
    nameAr: 'دينار كويتي',
    symbol: 'د.ك',
    minorUnitExponent: 3,
  },
  {
    code: 'BHD',
    name: 'Bahraini Dinar',
    nameAr: 'دينار بحريني',
    symbol: 'د.ب',
    minorUnitExponent: 3,
  },
  { code: 'OMR', name: 'Omani Rial', nameAr: 'ريال عماني', symbol: 'ر.ع', minorUnitExponent: 3 },
  {
    code: 'JOD',
    name: 'Jordanian Dinar',
    nameAr: 'دينار أردني',
    symbol: 'د.أ',
    minorUnitExponent: 3,
  },
];

export const SUPPORTED_CURRENCY_CODES: readonly string[] = CURRENCIES.map(
  (currency) => currency.code,
);

export const DEFAULT_CURRENCY_CODE = 'EGP';

const BY_CODE = new Map(CURRENCIES.map((currency) => [currency.code, currency]));

export function isSupportedCurrency(code: string): boolean {
  return BY_CODE.has(code);
}

/**
 * The definition for a code.
 *
 * Falls back to the default rather than throwing: a display path should never crash a page because
 * a tenant was configured with something unexpected, and the currency code travels with the data
 * anyway. Validation belongs at the write boundary, which is where the DTO enforces it.
 */
const FALLBACK: CurrencyDefinition = {
  code: DEFAULT_CURRENCY_CODE,
  name: 'Egyptian Pound',
  nameAr: 'جنيه مصري',
  symbol: 'E£',
  minorUnitExponent: 2,
};

export function currencyDefinition(code: string | null | undefined): CurrencyDefinition {
  return (code ? BY_CODE.get(code) : undefined) ?? BY_CODE.get(DEFAULT_CURRENCY_CODE) ?? FALLBACK;
}

/** 10^exponent — 100 for most currencies, 1000 for the dinars. */
export function minorUnitsPerMajor(code: string | null | undefined): number {
  return 10 ** currencyDefinition(code).minorUnitExponent;
}
